import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/dialog/dialog.js";

import { DataStore } from "@aws-amplify/datastore";
import {
    signIn,
    signUp,
    fetchAuthSession,
    confirmSignUp,
    getCurrentUser,
    fetchUserAttributes,
} from "aws-amplify/auth";
import { css, html } from "lit";
import { customElement, state, query } from "lit/decorators.js";
import { Kernel } from "../../Kernel";
import { validateEmail } from "../../Utils";
import { Style } from "../Styles";
import { validatePassword } from "./Utils";

import { choose } from "lit/directives/choose.js";
import { BaseElement } from "../BaseElement";
import { getModuleLog } from "../../Logger";

import "./ChangePassword";
import "./ForgotPassword";

type SignupParams = {
    givenName: string;
    familyName: string;
    email: string;
    password: string;
    password2: string;
};

enum AuthState {
    Unknown,
    SignedIn,
    SignedOut,
    SigningUp,
    NeedsVerification,
    PasswordReset,
}

@customElement("ds-auth-signin")
export class AuthSignin extends BaseElement {
    static override styles = [
        Style,
        css`
            .custom-input[invalid]:not([disabled])::part(label),
            .custom-input[invalid]:not([disabled])::part(help-text) {
                color: var(--sl-color-danger-600);
            }

            .custom-input[invalid]:not([disabled])::part(base) {
                border-color: var(--sl-color-danger-500);
            }

            .custom-input[invalid]:focus-within::part(base) {
                box-shadow: 0 0 0 var(--sl-focus-ring-width) var(--sl-color-danger-500);
            }

            .user-id {
                font-size: 0.8em;
                margin-bottom: 1em;
            }
        `,
    ];

    @state() protected _authState: number = AuthState.Unknown;
    @state() protected _authError = "";
    @state() protected _valid = false;
    @state() protected _currentUserEmail = "";
    @state() protected _currentUserPassword = "";
    @state() protected _currentUserId = "";
    @state() protected _changePasswordAuthError = "";

    @query("#change-password-dialog") protected _changePasswordDialog: HTMLInputElement | undefined;
    @query("#forgot-password-dialog") protected _forgotPasswordDialog: HTMLInputElement | undefined;
    @query("#reset-password-dialog") protected _resetPasswordDialog: HTMLInputElement | undefined;

    private _log = getModuleLog("auth");

    constructor() {
        super();
        Kernel.instance().events.subscribe("auth-changed", (event: string) => {
            if (event != "auth-user-sign-in-failure") {
                //this._init();
            }
        });

        this._init();
    }

    private async _init() {
        try {
            const user = await getCurrentUser();
            const session = await fetchAuthSession();
            const attributes = await fetchUserAttributes();
            this._log.debug(`current user is ${session}`);
            if (session && user && attributes && session.userSub && attributes.email) {
                this._authState = attributes.email_verified
                    ? AuthState.SignedIn
                    : AuthState.NeedsVerification;
                this._currentUserEmail = attributes.email;
                this._currentUserId = session.userSub;
            } else {
                this._authState = AuthState.SignedOut;
            }
        } catch (_: any) {
            this._log.debug("no user");
            this._authState = AuthState.SignedOut;
        }
    }

    private async _signOut() {
        this._kernel.signOut();
    }

    private _signupFormChanged() {
        this._valid = this._validateSignupFields();
    }

    private _getElementValue(name: string) {
        return (this.shadowRoot?.getElementById(name) as HTMLInputElement).value as string;
    }

    private _getSignupParams(): SignupParams {
        return {
            givenName: this._getElementValue("signup-given-name"),
            familyName: this._getElementValue("signup-family-name"),
            email: this._getElementValue("signup-email"),
            password: this._getElementValue("signup-password"),
            password2: this._getElementValue("signup-password2"),
        };
    }

    private async _signup(event: CustomEvent) {
        event.preventDefault();

        const params = this._getSignupParams();

        if (this._validateSignupFields()) {
            try {
                await signUp({
                    username: params.email,
                    password: params.password,
                    options: {
                        userAttributes: {
                            email: params.email, // optional
                            family_name: params.familyName,
                            given_name: params.givenName,
                        },
                    },
                });
                this._currentUserEmail = params.email;
                this._currentUserPassword = params.password;
                this._authState = AuthState.NeedsVerification;
            } catch (error: any) {
                this._authError = error.log;
                this._log.debug("error signing up", error);
            }
        }
    }

    private _validateSignupFields() {
        const params = this._getSignupParams();

        if (!validateEmail(params.email)) {
            this._authError = "Invalid email address";
        } else if (!validatePassword(params.password)) {
            this._authError = "Invalid password";
        } else if (!params.password2 || params.password != params.password2) {
            this._authError = "Passwords do not match";
        } else if (!params.givenName || params.givenName.length === 0) {
            this._authError = "Given name not specified";
        } else if (!params.familyName || params.familyName.length === 0) {
            this._authError = "Family name not specified";
        } else {
            this._authError = "";
            return true;
        }

        return false;
    }

    private _signinFormChanged() {
        this._valid = this._validateSigninFields();
    }

    private async _signin(event: Event) {
        event.preventDefault();
        const password = this.shadowRoot?.getElementById("password") as HTMLInputElement;
        const username = this.shadowRoot?.getElementById("username") as HTMLInputElement;

        if (this._validateSigninFields()) {
            try {
                const user = await signIn({ username: username.value, password: password.value });
                this._log.info(`signed in ${user}`);
                this._authState = AuthState.SignedIn;
                await DataStore.clear();
                await DataStore.start();
                Kernel.instance().events.raiseEvent("storage-local-data-cleared");
            } catch (error: any) {
                this._log.debug(typeof error, error);

                if (error.code === "UserNotConfirmedException") {
                    this._log.debug("user needs verification");
                    this._currentUserEmail = username.value;
                    this._authState = AuthState.NeedsVerification;
                    this._authError = error.log;
                    password.value = "";
                } else if (error.code === "PasswordResetRequiredException") {
                    this._currentUserEmail = username.value;
                    this._showDialog(this._resetPasswordDialog, true);
                } else {
                    this._authError = error;
                    this._log.debug("error signing in", error);
                }
            }
        }
    }

    private _validateSigninFields() {
        const PASSWORD_MIN_LENGTH = 6;
        const password = this.shadowRoot?.getElementById("password") as HTMLInputElement;
        const username = this.shadowRoot?.getElementById("username") as HTMLInputElement;

        if (!username || !validateEmail(username.value)) {
            this._authError = "Invalid email address";
        } else if (!password || password.value.length < PASSWORD_MIN_LENGTH) {
            this._authError = "Invalid password";
        } else {
            this._authError = "";
            return true;
        }

        return false;
    }

    private async _verify(event: CustomEvent) {
        event.preventDefault();
        const code = this.shadowRoot?.getElementById("verification-code") as HTMLInputElement;
        try {
            this._log.debug(`Validating user (${this._currentUserEmail}) with code (${code})`);
            await confirmSignUp({ username: this._currentUserEmail, confirmationCode: code.value });
            this._authState = AuthState.SignedOut;
            code.value = "";
        } catch (error) {
            this._log.debug("error confirming sign up", error);
        }
    }

    private _validateVerifyFields() {
        const code = this.shadowRoot?.getElementById("verification-code") as HTMLInputElement;
        return code && code.value.length;
    }

    private _showDialog(dialog: any, show: boolean): void {
        if (dialog) {
            if (show) {
                dialog.show();
            } else {
                dialog.hide();
            }
        }
    }

    override render() {
        return html`
            <div>
                <h3>${this.title}</h3>
                ${choose(this._authState, [
                    [AuthState.SignedOut, () => this._renderSignedOut()],
                    [AuthState.SignedIn, () => this._renderSignedIn()],
                    [AuthState.SigningUp, () => this._renderSigningUp()],
                    [AuthState.NeedsVerification, () => this._renderVerification()],
                ])}
            </div>
        `;
    }

    private _renderVerification() {
        return html`
            <div>
                <h3>Enter verification code sent to your email</h3>
                <form @submit=${this._verify}>
                    <sl-input
                        class="custom-input"
                        id="verification-code"
                        label="Verification code"
                        pill
                        clearable
                        size="large"
                        type="number"
                        placeholder="Enter code"
                        @sl-input="${this._validateVerifyFields}"
                        required
                        filled
                    >
                    </sl-input>
                    <div>
                        <h4
                            class="surface-error-color"
                            ?hidden="${!this._authError || this._authError.length === 0}"
                        >
                            ${this._authError}
                        </h4>
                    </div>
                    <br />
                    <div class="button-row">
                        <sl-button
                            variant="default"
                            @click=${() => (this._authState = AuthState.SignedOut)}
                            >Cancel</sl-button
                        >
                        <sl-button
                            style="margin-left:auto"
                            variant="success"
                            type="submit"
                            ?disabled="${!this._valid}"
                            >Verify</sl-button
                        >
                    </div>
                </form>
            </div>
        `;
    }

    private _renderChangePasswordDialog() {
        return html`<sl-dialog
            label="Change password"
            id="change-password-dialog"
            @on-cancel=${() => this._showDialog(this._changePasswordDialog, false)}
            @on-success=${() => this._showDialog(this._changePasswordDialog, false)}
            @sl-after-hide=${() =>
                (this.shadowRoot?.getElementById("auth-change-password") as any).reset()}
        >
            <ds-auth-change-password id="auth-change-password"></ds-auth-change-password>
        </sl-dialog>`;
    }

    private _renderForgotPasswordDialog() {
        return html`<sl-dialog
            label="Forgot password"
            id="forgot-password-dialog"
            @on-cancel=${() => this._showDialog(this._forgotPasswordDialog, false)}
            @on-success=${() => this._showDialog(this._forgotPasswordDialog, false)}
            @sl-after-hide=${() =>
                (this.shadowRoot?.getElementById("auth-forgot-password") as any).reset()}
        >
            <ds-auth-forgot-password id="auth-forgot-password"></ds-auth-forgot-password>
        </sl-dialog>`;
    }

    private _renderResetPasswordDialog() {
        return html`<sl-dialog
            label="Reset password"
            id="reset-password-dialog"
            @on-cancel=${() => this._showDialog(this._forgotPasswordDialog, false)}
            @on-success=${() => this._showDialog(this._forgotPasswordDialog, false)}
            @sl-after-hide=${() =>
                (this.shadowRoot?.getElementById("auth-forgot-password") as any).reset()}
        >
            <ds-auth-forgot-password
                id="auth-forgot-password"
                .email=${this._currentUserEmail}
                mode="submit"
            ></ds-auth-forgot-password>
        </sl-dialog>`;
    }

    private _renderSignedIn() {
        return html`
            ${this._renderChangePasswordDialog()}
            <div>
                <span>Signed in as ${this._currentUserEmail}</span>
                <div class="user-id">
                    <span>${this._currentUserId}</span
                    ><ds-copy-to-clipboard .text=${this._currentUserId}></ds-copy-to-clipboard>
                </div>
                <div class="button-row">
                    <sl-button
                        style="margin-left:auto"
                        variant="default"
                        @click="${() => this._showDialog(this._changePasswordDialog, true)}"
                        >Change password</sl-button
                    >
                    <sl-button variant="default" @click="${() => this._signOut()}"
                        >Sign out</sl-button
                    >
                </div>
            </div>
        `;
    }

    private _renderSignedOut() {
        return html`
            ${this._renderForgotPasswordDialog()} ${this._renderResetPasswordDialog()}
            <div>
                <form @submit=${this._signin}>
                    <sl-input
                        class="custom-input"
                        id="username"
                        name="username"
                        label="Email address"
                        clearable
                        size="medium"
                        type="email"
                        placeholder="bob@bob.net"
                        @sl-input="${this._signinFormChanged}"
                        required
                        filled
                        .value=${this._currentUserEmail}
                    >
                    </sl-input>
                    <br />
                    <sl-input
                        class="custom-input"
                        id="password"
                        name="password"
                        label="Password"
                        passwordToggle
                        required
                        size="medium"
                        type="password"
                        placeholder="********"
                        @sl-input="${this._signinFormChanged}"
                        filled
                        .value=${this._currentUserPassword}
                    >
                    </sl-input>
                    <div>
                        <h3
                            class="surface-error-color"
                            ?hidden="${!this._authError || this._authError.length === 0}"
                        >
                            ${this._authError}
                        </h3>
                    </div>
                    <br />
                    <div class="button-row">
                        <sl-button
                            variant="default"
                            @click="${() => (this._authState = AuthState.SigningUp)}"
                            >Sign up</sl-button
                        >

                        <sl-button
                            style="margin-left:auto"
                            variant="success"
                            type="submit"
                            ?disabled="${!this._valid}"
                            >Sign in</sl-button
                        >
                    </div>
                    <div class="button-row" style="padding-top:1em">
                        <sl-button
                            style="margin-left:auto"
                            variant="default"
                            size="small"
                            @click="${() => this._showDialog(this._forgotPasswordDialog, true)}"
                            >Forgot password</sl-button
                        >
                    </div>
                </form>
            </div>
        `;
    }

    private _renderSigningUp() {
        return html`
            <div>
                <h3>${this.title}</h3>
                <form @submit=${this._signup}>
                    <sl-input
                        class="custom-input"
                        id="signup-given-name"
                        label="Given name"
                        clearable
                        size="medium"
                        type="text"
                        placeholder="Bob"
                        @sl-input="${this._signupFormChanged}"
                        required
                        filled
                    >
                    </sl-input
                    ><br />
                    <sl-input
                        class="custom-input"
                        id="signup-family-name"
                        label="Family name"
                        clearable
                        size="medium"
                        type="text"
                        placeholder="McCave"
                        @sl-input="${this._signupFormChanged}"
                        required
                        filled
                    >
                    </sl-input
                    ><br />
                    <sl-input
                        class="custom-input"
                        id="signup-email"
                        label="Email"
                        clearable
                        size="medium"
                        type="email"
                        placeholder="bob@bob.net"
                        @sl-input="${this._signupFormChanged}"
                        required
                        filled
                    >
                    </sl-input
                    ><br />
                    <sl-input
                        class="custom-input"
                        id="signup-password"
                        label="Password"
                        passwordToggle
                        required
                        size="medium"
                        type="password"
                        placeholder="********"
                        @sl-input="${this._signupFormChanged}"
                        filled
                    >
                    </sl-input>
                    <br />
                    <sl-input
                        class="custom-input"
                        id="signup-password2"
                        label="Repeat password"
                        passwordToggle
                        required
                        size="medium"
                        type="password"
                        placeholder="********"
                        @sl-input="${this._signupFormChanged}"
                        filled
                    >
                    </sl-input>
                    <div>
                        <h4
                            class="surface-error-color"
                            ?hidden="${!this._authError || this._authError.length === 0}"
                        >
                            ${this._authError}
                        </h4>
                    </div>

                    <br />

                    <div class="button-row">
                        <sl-button
                            variant="neutral"
                            @click="${() => (this._authState = AuthState.SignedOut)}"
                            >Sign in</sl-button
                        >
                        <sl-button
                            style="margin-left:auto"
                            variant="success"
                            type="submit"
                            ?disabled="${!this._valid}"
                            >Sign up</sl-button
                        >
                    </div>
                </form>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-auth-signin": AuthSignin;
    }
}
